import { CircleAlert, CircleCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router';
import { PageHead } from '@/admin-area/ui/PageHead';
import { SidePanel } from '@/admin-area/ui/SidePanel';
import { useActiveContext } from '@/auth/ActiveContextProvider';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { OwnerDetailsSection } from '@/profile/OwnerDetailsSection';
import { EXPENSES_MANAGE, holds, useExpensesPermissions } from '../use-expenses-permissions';
import {
  listOwners,
  readOwner,
  writeOwner,
  type ManagedOwner,
  type ManagedOwnerEntry,
  type Place,
} from './api';

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly items: readonly ManagedOwnerEntry[] };

type Editing =
  | { readonly status: 'loading'; readonly entry: ManagedOwnerEntry }
  | { readonly status: 'error'; readonly entry: ManagedOwnerEntry }
  | { readonly status: 'ready'; readonly entry: ManagedOwnerEntry; readonly owner: ManagedOwner };

/**
 * «Dati per la nota spese» for Gestione (decision 192, #837): the organization's note owners, who
 * has filled in their address and tax code and who has not, and the same form as the profile's to
 * fill them in for someone — most notes are filed for others. Only for `expenses:expense:manage`;
 * the server checks it again on every call.
 */
export function OwnerDetailsPeoplePage() {
  const permissions = useExpensesPermissions();
  const { active } = useActiveContext();
  if (permissions.status === 'loading') return null;
  if (!holds(permissions, EXPENSES_MANAGE) || active === null) {
    return <Navigate to="/expenses" replace />;
  }
  return <OwnerDetailsPeople place={active} />;
}

function OwnerDetailsPeople({ place }: { readonly place: Place }) {
  const { t } = useTranslation('expenses');
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [editing, setEditing] = useState<Editing | null>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const load = useCallback(() => {
    let live = true;
    setList({ status: 'loading' });
    void listOwners(place).then((items) => {
      if (live) setList(items === null ? { status: 'error' } : { status: 'ready', items });
    });
    return () => {
      live = false;
    };
  }, [place]);

  useEffect(load, [load]);

  const open = (entry: ManagedOwnerEntry) => {
    setEditing({ status: 'loading', entry });
    void readOwner(place, entry.userId).then((owner) =>
      setEditing((current) =>
        current?.entry.userId !== entry.userId
          ? current
          : owner === null
            ? { status: 'error', entry }
            : { status: 'ready', entry, owner },
      ),
    );
  };

  // #952 M16: a note that cannot be sent links here with `?person=`, which opens that person once.
  const [search] = useSearchParams();
  const asked = useRef(search.get('person'));
  useEffect(() => {
    if (list.status !== 'ready' || asked.current === null) return;
    const entry = list.items.find((item) => item.userId === asked.current);
    asked.current = null;
    if (entry) open(entry);
  }, [list]);

  const markFilled = (userId: string, filled: boolean) =>
    setList((current) =>
      current.status === 'ready'
        ? {
            status: 'ready',
            items: current.items.map((item) =>
              item.userId === userId ? { ...item, filled } : item,
            ),
          }
        : current,
    );

  return (
    <div className="space-y-5">
      <PageHead title={t('ownerDetails.title')} summary={t('ownerDetails.summary')} />
      {list.status === 'loading' && (
        <div className="bg-muted/60 h-32 animate-pulse rounded-lg" aria-busy="true" />
      )}
      {list.status === 'error' && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm">{t('ownerDetails.loadFailed')}</p>
          <Button variant="outline" className="min-h-11" onClick={load}>
            {t('ownerDetails.retry')}
          </Button>
        </div>
      )}
      {list.status === 'ready' && list.items.length === 0 && (
        <p className="text-muted-foreground text-sm">{t('ownerDetails.empty')}</p>
      )}
      {list.status === 'ready' && list.items.length > 0 && (
        <PeopleList
          items={list.items}
          onlyMissing={onlyMissing}
          onToggleMissing={() => setOnlyMissing((current) => !current)}
          onOpen={open}
        />
      )}
      {editing !== null && (
        <SidePanel
          open
          onOpenChange={(next) => (next ? undefined : setEditing(null))}
          title={editing.entry.displayName}
          description={t('ownerDetails.panelSummary')}
          footer={null}
        >
          {editing.status === 'loading' && (
            <div className="bg-muted/60 h-48 animate-pulse rounded-lg" aria-busy="true" />
          )}
          {editing.status === 'error' && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm">{t('ownerDetails.readFailed')}</p>
              <Button variant="outline" className="min-h-11" onClick={() => open(editing.entry)}>
                {t('ownerDetails.retry')}
              </Button>
            </div>
          )}
          {editing.status === 'ready' && (
            <OwnerDetailsSection
              key={editing.entry.userId}
              ownerDetails={editing.owner.ownerDetails}
              title={t('ownerDetails.formTitle')}
              framed={false}
              clearConfirmation={{
                title: t('ownerDetails.clearTitle', { name: editing.entry.displayName }),
                ask: t('ownerDetails.clearAsk', { name: editing.entry.displayName }),
              }}
              write={(next) => writeOwner(place, editing.entry.userId, next)}
              onSaved={(details) => {
                markFilled(editing.entry.userId, details !== null);
                // The open panel keeps the saved details, so «Cancella i dati» follows them.
                setEditing((current) =>
                  current?.status === 'ready' && current.entry.userId === editing.entry.userId
                    ? { ...current, owner: { ...current.owner, ownerDetails: details } }
                    : current,
                );
              }}
            />
          )}
        </SidePanel>
      )}
    </div>
  );
}

/** Incomplete first: those are the people whose notes cannot be sent (M18). */
function PeopleList({
  items,
  onlyMissing,
  onToggleMissing,
  onOpen,
}: {
  readonly items: readonly ManagedOwnerEntry[];
  readonly onlyMissing: boolean;
  readonly onToggleMissing: () => void;
  readonly onOpen: (entry: ManagedOwnerEntry) => void;
}) {
  const { t } = useTranslation('expenses');
  const missing = items.filter((item) => !item.filled).length;
  const sorted = [...items]
    .sort((a, b) => Number(a.filled) - Number(b.filled))
    .filter((item) => !onlyMissing || !item.filled);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p
          role="status"
          className={cn(
            'inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold',
            missing > 0 ? 'bg-warning-tint text-warning' : 'text-success',
          )}
        >
          {missing > 0 ? <CircleAlert className="size-4" aria-hidden="true" /> : null}
          {missing > 0
            ? t('ownerDetails.missingCount', { count: missing })
            : t('ownerDetails.allFilled')}
        </p>
        {missing > 0 ? (
          <button
            type="button"
            aria-pressed={onlyMissing}
            onClick={onToggleMissing}
            className={cn(
              'inline-flex min-h-11 items-center rounded-full border px-4 text-sm font-medium',
              onlyMissing
                ? 'bg-selected text-selected-foreground border-selected-border'
                : 'border-border bg-card text-foreground',
            )}
          >
            {t('ownerDetails.onlyMissing')}
          </button>
        ) : null}
      </div>
      <ul className="border-border bg-card divide-border divide-y rounded-xl border">
        {sorted.map((item) => (
          <li key={item.userId} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <span className="min-w-0 flex-1 font-medium">{item.displayName}</span>
            {item.filled ? (
              <span className="text-success inline-flex items-center gap-1 text-sm">
                <CircleCheck className="size-4" aria-hidden="true" />
                {t('ownerDetails.filled')}
              </span>
            ) : (
              <span className="bg-warning-tint text-warning inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-semibold">
                <CircleAlert className="size-4" aria-hidden="true" />
                <span>{t('ownerDetails.missing')}</span>
                <span className="font-normal">· {t('ownerDetails.blocksSubmit')}</span>
              </span>
            )}
            <Button
              variant="outline"
              className="min-h-11"
              aria-label={t('ownerDetails.editFor', { name: item.displayName })}
              onClick={() => onOpen(item)}
            >
              {item.filled ? t('ownerDetails.edit') : t('ownerDetails.fill')}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
